import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';

export type DocumentType = 'PAYSLIP' | 'ORDER' | 'CERTIFICATE' | 'CONTRACT';

export interface Document {
  id: string;
  title: string;
  fileUrl?: string;
  isRead: boolean;
  readAt?: string | null;
  createdAt: string;
  metadata?: Record<string, any>;
  employee?: {
    id: string;
    first_name: string;
    last_name: string;
    middle_name?: string | null;
    employee_number?: string;
  };
  documentType?: {
    name: string;
    code_1c?: string | null;
  };
  fromCache?: boolean;
}

export interface RequestDocumentParams {
  employeeId: string;
  documentType: DocumentType;
  period?: string; // YYYY-MM для PAYSLIP
  documentId?: string; // для ORDER, CERTIFICATE
}

export interface RequestDocumentResponse {
  id: string;
  title: string;
  fileUrl?: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  fromCache: boolean;
  metadata?: Record<string, any>;
}

const DOCUMENTS_QUERY_KEY = 'documents';

/**
 * Запросить документ из 1С (on-demand с кэшированием)
 */
export const useRequestDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: RequestDocumentParams): Promise<RequestDocumentResponse> => {
      const { employeeId, documentType, period, documentId } = params;

      console.log('[useRequestDocument] Requesting:', { employeeId, documentType, period, documentId });

      let response: any;

      // Вызываем соответствующий метод API (админские эндпоинты с employeeId)
      switch (documentType) {
        case 'PAYSLIP':
          if (!period) throw new Error('Period is required for PAYSLIP');
          response = await apiClient.getPayslipDocument(employeeId, period);
          break;
        case 'ORDER':
          if (!documentId) throw new Error('Document ID is required for ORDER');
          response = await apiClient.getOrderDocument(employeeId, documentId);
          break;
        case 'CERTIFICATE':
          if (!documentId) throw new Error('Document ID is required for CERTIFICATE');
          response = await apiClient.getCertificateDocument(employeeId, documentId);
          break;
        case 'CONTRACT':
          response = await apiClient.getContractDocument(employeeId);
          break;
        default:
          throw new Error(`Unknown document type: ${documentType}`);
      }

      return response;
    },
    onSuccess: () => {
      // Инвалидируем список документов после успешного запроса
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });
    },
  });
};

/**
 * Получить список всех моих документов
 */
export const useMyDocuments = (filters?: {
  documentType?: string;
  period?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: [DOCUMENTS_QUERY_KEY, 'my-documents', filters],
    queryFn: async () => {
      const response = await apiClient.getMyDocuments(filters);
      return response;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * [ADMIN] Получить список всех документов организации
 */
export const useAllDocumentsAdmin = (filters?: {
  documentType?: string;
  period?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: [DOCUMENTS_QUERY_KEY, 'admin-all-documents', filters],
    queryFn: async () => {
      const response = await apiClient.getAllDocumentsAdmin(filters);
      return response;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Получить download URL для документа
 */
export const useDownloadDocument = () => {
  return useMutation({
    mutationFn: async (documentId: string): Promise<{ url: string }> => {
      // Формируем прямой URL для редиректа на MinIO signed URL
      const baseUrl = import.meta.env.VITE_API_URL || 'https://api.weekapp.kz';
      const token = localStorage.getItem('access_token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Not authenticated');
      }

      // Браузер сам сделает редирект на MinIO signed URL
      const url = `${baseUrl}/documents/${documentId}/download`;

      // Открываем в новом окне
      window.open(url, '_blank');

      return { url };
    },
  });
};
